import React, { CSSProperties, useMemo, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import LiveTooltip from 'live/components/ui/tooltip'
import { ColorStyle, HIDE_COLOR_VALUE, LiveValue } from './api'
import { getThresholdBreach, hexToRgba, THRESHOLD_TINT_ALPHA, ThresholdArrow } from './thresholds'
import { useCopyToClipboard } from './useCopyToClipboard'

type Props = {
    values: LiveValue[]
    boxDefaultColor?: string
    density?: string // 'small' | 'medium' | 'large', same values as the Cards view's box size
    colorStyle: ColorStyle
    colorTextEnabled: boolean
}

// null (unsorted) means the values are shown in the order configured in the Settings modal
type SortDirection = 'asc' | 'desc' | null

type ValueTableRowProps = {
    value: LiveValue
    boxDefaultColor?: string
    densityClassName: string
    colorStyle: ColorStyle
    colorTextEnabled: boolean
}

// its own component (rather than rendered inline inside a .map()) so useCopyToClipboard - a hook -
// can be called once per row safely, regardless of how many rows are in the table
const ValueTableRow = ({
    value,
    boxDefaultColor,
    densityClassName,
    colorStyle,
    colorTextEnabled
}: ValueTableRowProps): JSX.Element => {
    const breach = getThresholdBreach(
        value.value,
        value.minThresholds,
        value.maxThresholds,
        value.minThresholdColor,
        value.maxThresholdColor
    )
    const breachStyle = breach
        ? ({ '--row-breach-color': hexToRgba(breach.color, THRESHOLD_TINT_ALPHA) } as CSSProperties)
        : undefined
    const { tooltipContent, handleCopy } = useCopyToClipboard(String(value.value ?? '-'))
    const markColor = value.color ?? boxDefaultColor
    const tintColor = colorTextEnabled && markColor !== HIDE_COLOR_VALUE ? markColor : null

    return (
        <Tr className={breach ? 'breached' : ''} style={breachStyle}>
            <Td className={`color-column ${densityClassName}${colorStyle === 'bar' ? ' bar-style' : ''}`}>
                {colorStyle === 'circle' ? (
                    <ColorIndicator color={markColor} className={densityClassName} />
                ) : (
                    <ColorBar color={markColor} />
                )}
            </Td>
            {/* the tint goes on this inner span, not the Td itself - Td's own color is !important
                (to beat Live's base table styles), which would silently win over an inline style */}
            <Td className={densityClassName}>
                <span style={tintColor ? { color: tintColor } : undefined}>{value.title}</span>
            </Td>
            <Td className={`align-right ${densityClassName}`}>
                <ValueRow>
                    <LiveTooltip position="top" content={tooltipContent}>
                        <ValueText onClick={handleCopy}>{value.value ?? '-'}</ValueText>
                    </LiveTooltip>
                    <ThresholdArrow breach={breach} />
                    {value.value != null && value.uom ? <Unit>{value.uom}</Unit> : null}
                </ValueRow>
            </Td>
        </Tr>
    )
}

export const ValuesTable = React.memo(function ValuesTable({
    values,
    boxDefaultColor,
    density,
    colorStyle,
    colorTextEnabled
}: Props): JSX.Element {
    const [sortDirection, setSortDirection] = useState<SortDirection>(null)

    const densityClassName = `box-size-${density ?? 'medium'}`

    // cycle: unsorted -> ascending -> descending -> unsorted
    const handleToggleSort = (): void => {
        setSortDirection(current => (current === null ? 'asc' : current === 'asc' ? 'desc' : null))
    }

    const sortedValues = useMemo(() => {
        if (sortDirection == null) return values

        return [...(values ?? [])].sort((a, b) => {
            const comparison = (a.title ?? '').localeCompare(b.title ?? '')

            return sortDirection === 'asc' ? comparison : -comparison
        })
    }, [values, sortDirection])

    return (
        <TableContainer>
            <Table>
                <thead>
                    <tr>
                        <SortableTh colSpan={2} onClick={handleToggleSort} className={densityClassName}>
                            {i18n('Channel')}
                            {sortDirection && <i className={`fa fa-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`} />}
                        </SortableTh>
                        <Th className={`align-right ${densityClassName}`}>{i18n('Value')}</Th>
                    </tr>
                </thead>
                <tbody>
                    {sortedValues?.map(value => (
                        <ValueTableRow
                            key={`${value.layer}-${value.mnemonic ?? value.title}`}
                            value={value}
                            boxDefaultColor={boxDefaultColor}
                            densityClassName={densityClassName}
                            colorStyle={colorStyle}
                            colorTextEnabled={colorTextEnabled}
                        />
                    ))}
                </tbody>
            </Table>
        </TableContainer>
    )
})

const TableContainer = styled.div`
    height: calc(100% - 40px); // subtract the Header height
    width: 100%;
    border-radius: 0 0 4px 4px;
    background-color: var(--gray-200);
    overflow-x: hidden;
    overflow-y: auto; // border-radius + overflow clip the zebra rows to the rounded corners

    &::-webkit-scrollbar-track {
        background-color: transparent;
    }

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgb(180, 180, 180);
    }
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`

const Th = styled.th`
    position: sticky;
    top: 0;
    text-align: left;
    font-size: 11px;
    line-height: 18px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 10px 8px;
    color: var(--gray-800) !important;
    background-color: var(--gray-0) !important; // Live's own base table/th/td styles otherwise win by specificity
    border-bottom: 1px solid var(--gray-300);

    &.align-right {
        text-align: right;
        padding-right: 18px; // matches the Settings button's right inset
    }

    &.color-column {
        width: 12px;
        padding: 10px 0 10px 12px;
    }

    // density only adjusts vertical rhythm here (top/bottom padding), not the horizontal values
    // tuned above, so it composes with align-right/color-column regardless of class order
    &.box-size-small {
        padding-top: 5px;
        padding-bottom: 5px;
    }

    &.box-size-large {
        padding-top: 14px;
        padding-bottom: 14px;
    }
`

// spans the color-column + channel-name columns (colSpan={2}) so its own left padding fully
// controls where the label starts, independent of however the row's color column actually renders
const SortableTh = styled(Th)`
    cursor: pointer;
    user-select: none;
    padding-left: 12px; // matches the widget header's own left padding

    > i {
        margin-left: 4px;
        font-size: 10px;
        color: var(--blue-500); // same accent color as the header icons
    }
`

// each row alternates background color (no divider lines) for a smoother, borderless look
const Tr = styled.tr`
    &:nth-child(odd) {
        background-color: var(--gray-0) !important;
    }

    &:nth-child(even) {
        background-color: var(--gray-200) !important;
    }

    // a threshold breach overrides the zebra stripe above - equal specificity (one class + one
    // pseudo-class each) and declared later, so this wins the tie regardless of odd/even. The actual
    // color comes from the --row-breach-color custom property set inline per row (see the JSX above)
    &.breached {
        background-color: var(--row-breach-color) !important;
    }
`

const Td = styled.td`
    position: relative; // anchors ColorBar's absolute positioning in bar-style mode (color-column only)
    font-size: 14px;
    line-height: 21px;
    font-weight: 600;
    color: var(--gray-1000) !important;
    padding: 10px 8px;

    &.align-right {
        text-align: right;
        padding-right: 18px; // matches the Settings button's right inset
    }

    &.color-column {
        width: 12px; // matches ColorIndicator's own (medium) size, so the dot is never clipped
        padding: 10px 0 10px 12px;
    }

    // scoped to color-column specifically - box-size-small/large alone (below) also apply to the
    // title and value cells, which don't need their width constrained
    &.color-column.box-size-small {
        width: 9px;
    }

    &.color-column.box-size-large {
        width: 15px;
    }

    // the "bar" color style sits flush against the table's true left edge (no inset before it,
    // unlike the circle) - width is just the bar's own 4px, with the gap to the title text coming
    // entirely from the title Td's own left padding (8px) instead, so the two together (4 + 8) land
    // the title text at the same 12px the header's own padding-left uses. Declared after the
    // box-size-small/large width rules above (same specificity - two classes each) so it wins
    // regardless of which density is also active.
    &.color-column.bar-style {
        width: 4px;
        padding: 0;
    }

    &.box-size-small {
        padding-top: 5px;
        padding-bottom: 5px;
        font-size: 12px;
        line-height: 18px;
    }

    &.box-size-large {
        padding-top: 14px;
        padding-bottom: 14px;
        font-size: 16px;
        line-height: 24px;
    }
`

// base (medium) size matches the Settings modal's row color mark; small/large keep the same
// proportional relationship (0.75x / 1.25x) the density scale already used. 12/9/15 is a 25%
// reduction from the original 16/12/20.
const ColorIndicator = styled.span<{ color?: string }>`
    display: inline-block;
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background-color: ${({ color }) => (color === HIDE_COLOR_VALUE ? 'var(--gray-400)' : color ?? 'var(--gray-400)')};

    &.box-size-small {
        height: 9px;
        width: 9px;
    }

    &.box-size-large {
        height: 15px;
        width: 15px;
    }
`

// the "bar" color style's alternative to ColorIndicator - spans the color-column cell edge-to-edge,
// flush against the table's true left edge (no rounding - there's no visible gap around it to round
// into). Positioned absolutely (against the Td's own position:relative) rather than given
// height: 100%, since a percentage height doesn't reliably resolve against a <td>'s content-driven
// height - it renders as a real height:0 in practice, not a full-height bar.
const ColorBar = styled.span<{ color?: string }>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background-color: ${({ color }) => (color === HIDE_COLOR_VALUE ? 'var(--gray-400)' : color ?? 'var(--gray-400)')};
`

// a flex row inside the Value cell - only ValueText shrinks/truncates, ThresholdArrow and Unit
// always stay fully visible. justify-content keeps the group flush right, matching Td.align-right
const ValueRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
`

const ValueText = styled.span`
    cursor: pointer; // click to copy the full value
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0; // lets a flex child actually shrink/truncate instead of overflowing its row
`

const Unit = styled.span`
    flex-shrink: 0;
    font-size: 10px;
    line-height: 15px;
    font-weight: 400;
    margin-left: 2px;
`
